import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, Merge, Mail, Phone, AlertTriangle, CheckCircle2, RefreshCw
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITIES = [
  { key: 'customers', label: 'Customers', endpoint: '/api/admin/duplicates/customers', mergeEndpoint: '/api/admin/customers/merge' },
  { key: 'cleaners', label: 'Cleaners', endpoint: '/api/admin/duplicates/cleaners', mergeEndpoint: '/api/admin/cleaners/merge' },
  { key: 'quote-requests', label: 'Quote Requests', endpoint: '/api/admin/duplicates/quote-requests', mergeEndpoint: '/api/admin/quote-requests/merge' },
];

const AdminDuplicatesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/settings');
  const [tab, setTab] = useState('customers');
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState({});
  const [mergeDialog, setMergeDialog] = useState(null); // { entityKey, group, winnerId }
  const [merging, setMerging] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchGroups = useCallback(async (entityKey) => {
    const ent = ENTITIES.find(e => e.key === entityKey);
    if (!ent) return;
    setLoading(l => ({ ...l, [entityKey]: true }));
    try {
      const res = await axios.get(`${API_URL}${ent.endpoint}`, { headers });
      setGroups(g => ({ ...g, [entityKey]: res.data.groups || [] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to load ${ent.label} duplicates`);
    } finally {
      setLoading(l => ({ ...l, [entityKey]: false }));
    }
  }, [headers]);

  useEffect(() => { fetchGroups(tab); }, [tab, fetchGroups]);

  const openMerge = (entityKey, group, winnerId) => {
    setMergeDialog({ entityKey, group, winnerId });
  };

  const runMerge = async () => {
    if (!mergeDialog) return;
    const { entityKey, group, winnerId } = mergeDialog;
    const loserId = group.members.find(m => m.id !== winnerId)?.id;
    if (!loserId) { toast.error('Missing loser'); return; }
    const ent = ENTITIES.find(e => e.key === entityKey);
    setMerging(true);
    try {
      const res = await axios.post(`${API_URL}${ent.mergeEndpoint}`, {
        winner_id: winnerId, loser_id: loserId,
      }, { headers });
      const moved = res.data.moved || {};
      const total = Object.values(moved).reduce((s, n) => s + (typeof n === 'number' ? n : 0), 0);
      toast.success(`Merged. ${total} record${total === 1 ? '' : 's'} reassigned to the winner. Loser hard-deleted.`);
      setMergeDialog(null);
      fetchGroups(entityKey);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Merge failed');
      toast.error(msg);
    } finally { setMerging(false); }
  };

  const totalDupes = (groups[tab] || []).length;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-duplicates-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={back} className="text-zinc-500" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchGroups(tab)} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <Merge className="h-6 w-6 text-[#F56F05]" />
          <h1 className="text-3xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Duplicate Merge</h1>
        </div>
        <p className="text-sm text-zinc-500 mb-6">
          Detect and merge duplicate records based on matching email or phone. The loser record is <strong>hard-deleted</strong> after merge and all its data (invoices, appointments, notes, etc.) is reassigned to the winner. Records with conflicting Stripe accounts are refused for manual review.
        </p>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="dupes-tabs">
            {ENTITIES.map(e => (
              <TabsTrigger key={e.key} value={e.key} data-testid={`tab-${e.key}`}>{e.label}</TabsTrigger>
            ))}
          </TabsList>

          {ENTITIES.map(e => (
            <TabsContent key={e.key} value={e.key} className="mt-6">
              {loading[e.key] ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-[#F56F05]" />
                </div>
              ) : totalDupes === 0 ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-12 text-center">
                    <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-3" />
                    <p className="text-zinc-600 font-medium mb-1">No duplicates found</p>
                    <p className="text-zinc-400 text-sm">Your {e.label.toLowerCase()} records look clean.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-zinc-500" data-testid={`count-${e.key}`}>
                    Found {totalDupes} duplicate group{totalDupes === 1 ? '' : 's'}
                  </p>
                  {(groups[e.key] || []).map((grp, i) => (
                    <DupeGroupCard
                      key={`${grp.match_key}-${i}`}
                      group={grp}
                      onMerge={(winnerId) => openMerge(e.key, grp, winnerId)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Dialog open={!!mergeDialog} onOpenChange={(o) => { if (!o) setMergeDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="merge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Confirm merge
            </DialogTitle>
            <DialogDescription>
              This action is <strong>permanent</strong>. The loser record will be hard-deleted and all its data (invoices, appointments, notes, addresses) reassigned to the winner. Cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {mergeDialog && (
            <MergeConfirmContent dialog={mergeDialog} />
          )}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setMergeDialog(null)} disabled={merging}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={runMerge} disabled={merging} data-testid="confirm-merge-btn">
              {merging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Merge className="h-4 w-4 mr-2" />}
              Confirm merge
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DupeGroupCard = ({ group, onMerge }) => (
  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
    <Card className="border-0 shadow-sm" data-testid={`dupe-group-${group.match_key}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-[10px] uppercase">
            {group.match_type}
          </Badge>
          <span className="text-xs font-mono text-zinc-500 truncate">{group.match_key}</span>
          <span className="text-xs text-zinc-400 ml-auto">{group.members.length} records</span>
        </div>
        <div className="space-y-2">
          {group.members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg bg-zinc-50" data-testid={`member-${m.id}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 text-zinc-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                {(m.full_name || m.email || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900 truncate">{m.full_name || '(no name)'}</p>
                <div className="flex flex-wrap gap-2 text-[11px] text-zinc-500">
                  {m.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{m.email}</span>}
                  {m.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{m.phone}</span>}
                  {m.created_at && <span className="text-zinc-400">created {new Date(m.created_at).toLocaleDateString()}</span>}
                  {m.status && <Badge variant="outline" className="text-[10px]">{m.status}</Badge>}
                </div>
              </div>
              <Button
                size="sm"
                className="bg-[#F56F05] hover:bg-[#C2410C] text-white text-xs rounded-full"
                onClick={() => onMerge(m.id)}
                data-testid={`keep-${m.id}`}
              >
                Keep this — merge others
              </Button>
            </div>
          ))}
          {group.members.length > 2 && (
            <p className="text-[11px] text-zinc-400 pl-2">
              Note: merges are 1-to-1. Merge two at a time; run again to fold the remaining record in.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const MergeConfirmContent = ({ dialog }) => {
  const { group, winnerId } = dialog;
  const winner = group.members.find(m => m.id === winnerId);
  const loser = group.members.find(m => m.id !== winnerId);
  if (!winner || !loser) return null;
  return (
    <div className="space-y-3 py-2">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg bg-green-50 border border-green-200">
          <p className="text-[10px] font-semibold text-green-700 uppercase mb-1">Keep (winner)</p>
          <p className="text-sm font-semibold text-zinc-900">{winner.full_name || '(no name)'}</p>
          <p className="text-xs text-zinc-500">{winner.email || '—'}</p>
          <p className="text-xs text-zinc-500">{winner.phone || '—'}</p>
        </div>
        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-[10px] font-semibold text-red-700 uppercase mb-1">Delete (loser)</p>
          <p className="text-sm font-semibold text-zinc-900">{loser.full_name || '(no name)'}</p>
          <p className="text-xs text-zinc-500">{loser.email || '—'}</p>
          <p className="text-xs text-zinc-500">{loser.phone || '—'}</p>
        </div>
      </div>
      <p className="text-xs text-zinc-500">
        Match reason: <span className="font-mono">{group.match_type} = {group.match_key}</span>
      </p>
    </div>
  );
};

export default AdminDuplicatesPage;
